/**
 * Low-level Meta layer: fbq events, consent, and the cookies Meta sets.
 *
 * The pixel has no cookieless mode. Any un-suppressed track call writes _fbp
 * to the device, so unlike Google this layer cannot rely on a consent signal
 * to make a pre-consent event harmless. index.js holds events instead; this
 * module only reports whether firing is currently allowed.
 */

import Cookie from 'js-cookie';

import { removeCookieEverywhere } from '../cookies';


const hasFbq = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

// Whether this tab has the pixel. A granted consent cookie only proves that
// some tab loaded it; index.js checks this before trusting the cookie.
export const isLoaded = () => hasFbq();

export const getPixelId = () => process.env.GATSBY_PUBLIC_FACEBOOK_PIXEL;

/* Deploy previews inherit the production pixel id, and this loader replaced a
   head snippet that html.js only rendered on non-preview builds. Same rule as
   the Google tags there: a preview stays silent unless staging opts in with
   GATSBY_PUBLIC_ANALYTICS_ENABLED. Literal env reads, see gtag.js. */
export const isPixelAllowed = () => (
  process.env.GATSBY_PUBLIC_ANALYTICS_ENABLED === 'true' || process.env.GATSBY_PUBLIC_IS_PREVIEW !== 'true'
);

/**
 * Loads the pixel, and only then.
 *
 * This used to sit in the document head and run on every page load. That meant
 * a request to Meta, carrying the visitor's IP, before the guest had decided
 * anything, and on builds with no pixel configured at all. Google can be
 * loaded eagerly because Consent Mode v2 makes a denied-state ping cookieless
 * and identifier-free; Meta has no equivalent, so the only way to honour
 * "these cookies are only set with your express prior consent" is not to
 * fetch it until consent exists.
 *
 * Idempotent, and a no-op without a pixel id, which is what keeps a build with
 * no environment variables from talking to Meta at all.
 */
export function loadPixel() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  const pixelId = getPixelId();
  if (!pixelId || !isPixelAllowed()) return false;
  if (window.fbq) return true;

  // Meta's stub, so calls made before fbevents.js arrives are queued rather
  // than lost. Written out rather than pasted from their snippet because that
  // version assumes a <script> already exists in the document to insert before.
  const fbq = function fbq(...args) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  // eslint-disable-next-line no-underscore-dangle
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) first.parentNode.insertBefore(script, first);
  else document.head.appendChild(script);

  window.fbq('init', pixelId);
  return true;
}

/* Meta's own cookies, read for the Conversions API so the server-side Purchase
   can be matched to the same browser and deduplicated against this one.
   _fbp only exists once the pixel has run, so it is absent until marketing
   consent is given, which is correct: with no consent there is no server event
   either. */
export const getFbp = () => Cookie.get('_fbp') || null;

export const getFbc = () => Cookie.get('_fbc') || null;

export function track(eventName, params = {}, options = {}) {
  if (!hasFbq()) return;
  window.fbq('track', eventName, params, options);
}

function deleteMetaCookies() {
  // _fbc is the click-id cookie and was previously left behind on revoke. Meta
  // sets both on the registrable domain, so they need a domain-aware removal.
  ['_fbp', '_fbc'].forEach(removeCookieEverywhere);
}

/**
 * Marketing consent alone gates the pixel.
 *
 * This previously required analytics AND marketing, so a guest who ticked only
 * marketing, the box that exists for exactly this, got no pixel at all. Google
 * has always read the two categories separately; this brings Meta in line and
 * matches what the banner tells the guest each box does.
 */
export function updateMetaConsent({ marketing }) {
  if (typeof window === 'undefined') return false;

  const hadPixel = hasFbq();
  // Fetched at the moment of consent, not before it.
  if (marketing) loadPixel();

  if (hasFbq()) {
    // https://developers.facebook.com/docs/meta-pixel/implementation/gdpr/
    window.fbq('consent', marketing ? 'grant' : 'revoke');
  }

  if (!marketing) deleteMetaCookies();

  // True exactly when this call is what loaded the pixel, so the caller can
  // send the one PageView Meta's own snippet would have sent on load.
  return Boolean(marketing && !hadPixel && hasFbq());
}

export function initMeta({ marketing }) {
  if (typeof window === 'undefined') return;
  // A returning guest who already granted consent gets the pixel on load; a
  // new or declining one gets no request to Meta at all.
  updateMetaConsent({ marketing });
}
